import { useState, type FormEvent } from 'react';
import { OutcomeView, QuotaMeter, toOutcome, useSession, type Outcome, type QuotaSnapshot } from '@huaca/ui';
import { config } from './config';

type Op = '+' | '-' | '*' | '/';

/**
 * The product itself. Everything platform-shaped (auth, entitlement, seats, quota) is handled by
 * @huaca/ui and the gate — this component only does arithmetic and renders what the server says.
 */
export function Calculator({ onShowPlans }: { onShowPlans: () => void }) {
  const { client, can, reload } = useSession();
  const [a, setA] = useState('8');
  const [b, setB] = useState('4');
  const [op, setOp] = useState<Op>('+');
  const [result, setResult] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [quota, setQuota] = useState<QuotaSnapshot | null>(null);
  const [busy, setBusy] = useState(false);

  // Server-resolved, never guessed from a role name: a `calculator.viewer` can read history but not
  // calculate, so the button is absent rather than present-and-403ing.
  const mayCalculate = can('calculator:calculate:run');
  const mayHistory = can('calculator:history:read');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setOutcome(null);
    try {
      const { data, quota: q } = await client.requestWithQuota<{ result: number }>(
        `/api/${config.productId}/calculate`,
        { method: 'POST', body: JSON.stringify({ a: Number(a), b: Number(b), op }) },
      );
      setResult(data.result);
      setQuota(q);
    } catch (err) {
      // One mapping, in the platform: NO_ENTITLEMENT → plans, NO_SEAT → ask an admin,
      // QUOTA_EXCEEDED → upsell. Never branch on the HTTP status here.
      const o = toOutcome(err);
      setOutcome(o);
      // A 429 means the meter is now exactly zero — say so rather than leaving a stale "1 left".
      if (o.kind === 'quota-exceeded' && o.quota) {
        setQuota({ limit: o.quota.limit, remaining: 0, window: o.quota.window, resetAt: o.quota.resetAt });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="calculator">
      <h1>{config.productName}</h1>
      {/* Only after a real call — we learn the limits from response headers, never from hardcoded
          copy that would drift from the plan's actual quota. */}
      {quota && (
        <QuotaMeter limit={quota.limit} remaining={quota.remaining} window={quota.window} resetAt={quota.resetAt} />
      )}

      <OutcomeView outcome={outcome} onShowPlans={onShowPlans} onRetry={() => reload()}>
        <div className="huaca-calc">
          {mayCalculate ? (
            <form className="huaca-calc-form" onSubmit={submit}>
              <div className="huaca-field">
                <label htmlFor="calc-a">מספר ראשון</label>
                <input id="calc-a" type="number" inputMode="decimal" dir="ltr" value={a} onChange={(e) => setA(e.target.value)} required />
              </div>

              <div className="huaca-field">
                <label htmlFor="calc-op">פעולה</label>
                <select id="calc-op" value={op} onChange={(e) => setOp(e.target.value as Op)}>
                  <option value="+">+</option>
                  <option value="-">−</option>
                  <option value="*">×</option>
                  <option value="/">÷</option>
                </select>
              </div>

              <div className="huaca-field">
                <label htmlFor="calc-b">מספר שני</label>
                <input id="calc-b" type="number" inputMode="decimal" dir="ltr" value={b} onChange={(e) => setB(e.target.value)} required />
              </div>

              <button type="submit" className="btn-primary" disabled={busy} data-testid="calc-submit">
                {busy ? 'מחשב…' : 'חישוב'}
              </button>
            </form>
          ) : (
            /* A viewer lands here: they hold a seat and can read history, just not calculate. */
            <p data-testid="calc-read-only">
              התפקיד שלכם בארגון הוא לצפייה בלבד עבור {config.productName}.
            </p>
          )}

          {result !== null && (
            <p data-testid="calc-result">
              תוצאה: <strong>{result}</strong>
            </p>
          )}
        </div>
      </OutcomeView>

      {mayHistory && <History />}
    </div>
  );
}

function History() {
  const { client } = useSession();
  const [items, setItems] = useState<{ a: number; b: number; op: string; result: number }[] | null>(null);

  return (
    <section>
      <h2>היסטוריה</h2>
      <button
        type="button"
        data-testid="load-history"
        onClick={async () => {
          const res = await client.request<{ items: typeof items }>(`/api/${config.productId}/history`);
          setItems(res.items ?? []);
        }}
      >
        טעינת היסטוריה
      </button>
      {items && (
        <ul className="huaca-history" data-testid="history-list" dir="ltr">
          {items.map((i, idx) => (
            <li key={idx}>
              {i.a} {i.op} {i.b} = {i.result}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
